import { WeekDay } from '@common/models/Enums';
import { DateTime, DurationUnit } from 'luxon';

/**
 * Generate all days between two dates.
 * @param start the starting date. Time information is ignored.
 * @param end the ending date.
 * @param inclusive whether to inclue the end date in the resulting list.
 */
function* allDaysBetweenImpl(
  start: DateTime,
  end: DateTime,
  inclusive: boolean = true,
) {
  let cursor = start.startOf('day');
  while (inclusive ? cursor <= end : cursor < end) {
    yield cursor;
    cursor = cursor.plus({ days: 1 });
  }
}

export const sortAscDateTimesImpl = (a: DateTime, b: DateTime) =>
  a < b ? -1 : a > b ? 1 : 0;
export const sortDescDateTimesImpl = (b: DateTime, a: DateTime) =>
  a < b ? -1 : a > b ? 1 : 0;
// DayOfWeek: 0=Sunday; 6=Saturday
// DateTime.Weekday: 1=Monday; 7=Sunday
// Mod DateTime.Weekday by 7 to get equivalent DayOfWeek.
export const fallsOnDayOfWeekImpl = (dayOfWeek: WeekDay.Day, date: DateTime) =>
  dayOfWeek === date.weekday % 7;

/**
 * A namespace for DateTime Utilities, previous functions that were directly exported were left alone and implemented in the namespace and reexported to prevent refactoring everything that uses them.
 */
namespace DateTimeUtils {
  export const allDaysBetween = allDaysBetweenImpl;

  export const fallsOnDayOfWeek = fallsOnDayOfWeekImpl;

  export const sortAscDateTimes = sortAscDateTimesImpl;

  export const sortDescDateTimes = sortDescDateTimesImpl;

  export const getZoneDateAsLocal = (
    dateVal: string | DateTime,
    timeZone: string,
  ): DateTime => {
    let date: DateTime;
    if (typeof dateVal === 'string') {
      const utcDateVal = DateTime.fromISO(dateVal as string, { zone: 'UTC' });
      const hospitalDateVal = utcDateVal.setZone(timeZone, {
        keepLocalTime: false,
      });
      date = hospitalDateVal;
    } else date = dateVal.setZone(timeZone, { keepLocalTime: false });
    const objectDate = date.toObject(); //convert to object which doesn't have any time offsets
    const forcedLocalDate = DateTime.fromObject(objectDate); //using local browser timezone, load the object from the specified timezone as if it were that time locally
    return forcedLocalDate;
  };

  /**
   * Convert an ISO Date or a DateTime to a time in the specified offset, then converts back to UTC while keeping the time the same from the timezone
   * Put another way, if you have an Date that's 6 AM in Eastern time, this convert's it to say "America/Phoenix" which would be 4 AM
   * And then converts it to UTC while maintaining the time of 4AM
   * This allows us to keep Calendar in UTC zone and give it UTC selected Date's etc in specific time zones, basically making timezones a non thing
   * and allowing the calendar to act like it's in the timezone where the hospital is.
   * There are bugs with FullCalendar and Luxon preventing us from using Luxon for TimeZone stuff and JS Date's don't have time zones, just browser local and utc.
   * @param dateVal The ISO Date string or DateTime you want to convert to another timezone and then shift to UTC
   * @param timeZone  The timezone you want to shift the time in UTC too, (i.e. you can go from any offset/timesone on dateVal to whatever timezone you specify here IANA timezone format (matches petpass db on Store table))
   * @returns
   */
  export const keepTimeInZoneAsUtc = (
    dateVal: string | DateTime,
    timeZone: string,
  ): DateTime => {
    let date: DateTime;
    if (typeof dateVal === 'string')
      date = DateTime.fromISO(dateVal as string, { zone: timeZone });
    else date = dateVal.setZone(timeZone, { keepLocalTime: false });
    const utcDate = date.setZone('UTC', { keepLocalTime: true }); //important, keepLocalTime keeps the time on conversions
    return utcDate;
  };

  export const getAsDateTimeUTC = (
    dateTimeOrString: DateTime | string | undefined | null,
  ) => {
    if (!dateTimeOrString) return null;
    const dateTime =
      typeof dateTimeOrString === 'string'
        ? DateTime.fromISO(dateTimeOrString, { zone: 'UTC' })
        : (dateTimeOrString as DateTime);
    return dateTime;
  };

  export const getDayOfWeek = (date: DateTime): WeekDay.Day => {
    return (date.weekday % 7) as WeekDay.Day;
  };

  export const getDuration = (
    startDate: string | DateTime,
    endDate: string | DateTime,
    durationUnits: DurationUnit,
  ): number => {
    const start =
      typeof startDate == 'string'
        ? DateTime.fromISO(startDate, { zone: 'UTC' })
        : startDate;
    const end =
      typeof endDate == 'string'
        ? DateTime.fromISO(endDate, { zone: 'UTC' })
        : endDate;
    const duration = end.diff(start, durationUnits);
    return duration.as(durationUnits);
  };

  export const getOldestDateTime = (
    firstDate: string | DateTime,
    secondDate: string | DateTime,
  ): DateTime => {
    const first =
      typeof firstDate == 'string'
        ? DateTime.fromISO(firstDate, { zone: 'UTC' })
        : firstDate;
    const second =
      typeof secondDate == 'string'
        ? DateTime.fromISO(secondDate, { zone: 'UTC' })
        : secondDate;

    const firstDuration = DateTimeUtils.getDuration(
      first,
      DateTime.utc(),
      'minutes',
    );
    const secondDuration = DateTimeUtils.getDuration(
      second,
      DateTime.utc(),
      'minutes',
    );

    if (firstDuration > secondDuration) {
      return first;
    } else {
      return second;
    }
  };

  /**
   * Convert 24-hour DateTime to 12 hour string.
   * @param dateVal always 24-hour DateTime
   * @returns format like '09:30 AM'
   */
  export const getTimehour12 = (dateVal: DateTime): string => {
    return (
      (dateVal.hour % 12 ? (dateVal.hour % 12).toString() : '12') +
      ':' +
      (dateVal.minute < 10
        ? '0' + dateVal.minute.toString()
        : dateVal.minute.toString()) +
      (dateVal.hour >= 12 ? ' PM' : ' AM')
    );
  };

  export const objToDateTime = <T>(inputValues: T): T => {
    if (!inputValues) {
      return inputValues;
    }
    const transformValue = (value: any): any => {
      if (value === undefined) {
        return null;
      } else if (Array.isArray(value)) {
        return value.map((item) => transformValue(item));
      } else if (value instanceof Date) {
        // was returning {} instead of Date obj
        return value;
      } else if (DateTime.isDateTime(value)) {
        return DateTime.fromObject((value as any).c, { zone: 'utc' });
      } else if (typeof value === 'object') {
        return objToDateTime(value);
      } else {
        return value;
      }
    };

    return Object.fromEntries(
      Object.entries(inputValues).map(([key, value]) => [
        key,
        transformValue(value),
      ]),
    ) as T;
  };
}

const getRoundedDate = (minutes: number, d = new Date()) => {
  if (!(d instanceof Date)) d = new Date();
  let ms = 1000 * 60 * minutes; // convert minutes to ms
  let roundedDate = new Date(Math.round(d.getTime() / ms) * ms);

  return roundedDate;
};

const getOutOfBoundsDate = (
  time: Date,
  openTime: string,
  closeTime: string,
) => {
  const openingHours = DateTime.fromISO(openTime).toJSDate();
  const closingHours = DateTime.fromISO(closeTime).toJSDate();

  const greaterThanMin = openingHours.getTime() < time.getTime();
  const lessThanMax = time.getTime() < closingHours.getTime();

  if (!greaterThanMin) return openingHours;
  if (!lessThanMax) return closingHours;
  return undefined;
};

export const resetDateTimeTimeZoneToCurrentTimezone = (
  date: DateTime,
  timeZone: string,
) => {
  // Both dates:
  // "2023-04-20T07:45:00.000Z"
  // "2023-04-20T07:45:00.000-10:00"
  // Will convert to:
  // "2023-04-20T07:45:00.000-10:00"
  const fixedDateAsTimeZone = date.setZone(timeZone, { keepLocalTime: true });
  return fixedDateAsTimeZone;
};

export const checkIsToday = ({
  timeZone,
  compareDate,
}: {
  timeZone: string;
  compareDate: DateTime | undefined;
}) => {
  const visitDateAsLocalTime = compareDate
    ? resetDateTimeTimeZoneToCurrentTimezone(compareDate, timeZone)
    : undefined;
  const todayShiftedAtTimeZone = resetDateTimeTimeZoneToCurrentTimezone(
    DateTime.now().setZone(timeZone),
    timeZone,
  );

  return (
    todayShiftedAtTimeZone.toLocaleString(DateTime.DATE_SHORT) ===
    visitDateAsLocalTime?.toLocaleString(DateTime.DATE_SHORT)
  );
};

/* timezone range for the application currently:
 pacific, mountain, central, eastern, utc
 -7     , -6      , -5     , -4     , 0
 this leads to days changing during the following day range.
 5pm    , 6pm     , 7pm    , 8pm    , 11:59pm - time when day changes.
 7am utc, 6am utc , 5am utc, 4am utc, 11:59pm utc, - utc time when each timezone hits midnight.
*/

const todayDateTime = () => DateTime.utc();
const yesterday = () => todayDateTime().minus({ days: 1 }).toISO();
const today = () => todayDateTime().toISO();
const tomorrow = () => todayDateTime().plus({ days: 1 }).toISO();
const dayAfterTomorrow = () => todayDateTime().plus({ days: 2 }).toISO();
const maxExpirationDate = () => {
  return todayDateTime().plus({ years: 1 }).toJSDate();
};

export {
  DateTimeUtils,
  allDaysBetweenImpl as allDaysBetween, //reexport the original functions so nothing breaks (TODO, refactor later to just use the namespace)
  fallsOnDayOfWeekImpl as fallsOnDayOfWeek,
  yesterday,
  today,
  tomorrow,
  dayAfterTomorrow,
  getRoundedDate,
  getOutOfBoundsDate,
  maxExpirationDate,
};
